import useAuth from "@/store/use-auth";
import useVessel from "@/store/use-vessel";
import { hasPermission, usePermissionsStore } from "@/store/use-permissions";

interface UseHasPermissionOptions {
  /** Specific vessel ID to check permissions for. If not provided, uses the currently selected vessel. */
  vesselId?: string;
  /** Skip vessel-level permission check (only check org-level). Default: false */
  skipVesselCheck?: boolean;
}

/**
 * Hook to check if the current user has a specific permission.
 * Supports vessel-specific permission overrides.
 *
 * When permissions are not yet loaded from the API, falls back to JWT claims
 * for backward compatibility during the transition period.
 *
 * @param permission - The permission code to check (e.g., "accounts.read")
 * @param options - Optional configuration
 * @returns true if the user has the permission, false otherwise
 *
 * @example
 * const canWriteExpenses = useHasPermission(PERMISSIONS.Expenses.Write);
 *
 * @example
 * const canApprove = useHasPermission("budgets.approve", { vesselId: "vessel-uuid" });
 */
export function useHasPermission(
  permission: string,
  options?: UseHasPermissionOptions
): boolean {
  const permissionsState = usePermissionsStore();
  const selectedVessel = useVessel((state) => state.selectedVessel);
  const user = useAuth((state) => state.user);

  // Fallback to JWT claims if permissions not loaded yet (backward compatibility / offline)
  if (!permissionsState.isLoaded) {
    const jwtPermissions = user?.permissions ?? [];
    return jwtPermissions.some(
      (p) => p.toLowerCase() === permission.toLowerCase()
    );
  }

  // Determine vessel ID for permission check
  const vesselId = options?.skipVesselCheck
    ? undefined
    : (options?.vesselId ?? selectedVessel?.id);

  return hasPermission(permissionsState, permission, vesselId);
}

/**
 * Hook to check if the current user has ALL of the specified permissions.
 */
export function useHasAllPermissions(
  permissions: string[],
  options?: UseHasPermissionOptions
): boolean {
  const permissionsState = usePermissionsStore();
  const selectedVessel = useVessel((state) => state.selectedVessel);
  const user = useAuth((state) => state.user);

  // Fallback to JWT claims if permissions not loaded yet
  if (!permissionsState.isLoaded) {
    const jwtPermissions = user?.permissions ?? [];
    return permissions.every((p) =>
      jwtPermissions.some((jp) => jp.toLowerCase() === p.toLowerCase())
    );
  }

  const vesselId = options?.skipVesselCheck
    ? undefined
    : (options?.vesselId ?? selectedVessel?.id);

  return permissions.every((p) => hasPermission(permissionsState, p, vesselId));
}

/**
 * Hook to check if the current user has ANY of the specified permissions.
 */
export function useHasAnyPermission(
  permissions: string[],
  options?: UseHasPermissionOptions
): boolean {
  const permissionsState = usePermissionsStore();
  const selectedVessel = useVessel((state) => state.selectedVessel);
  const user = useAuth((state) => state.user);

  // Fallback to JWT claims if permissions not loaded yet
  if (!permissionsState.isLoaded) {
    const jwtPermissions = user?.permissions ?? [];
    return permissions.some((p) =>
      jwtPermissions.some((jp) => jp.toLowerCase() === p.toLowerCase())
    );
  }

  const vesselId = options?.skipVesselCheck
    ? undefined
    : (options?.vesselId ?? selectedVessel?.id);

  return permissions.some((p) => hasPermission(permissionsState, p, vesselId));
}
